import { BookOpenIcon } from '@heroicons/react/24/outline';
import Link from 'next/link';
import { useRouter } from 'next/router';
import { useEffect, useState } from 'react';
import { assertUnreachable } from '../../../utils/assert';
import type {
  TableOfContentsListItemNode,
  TableOfContentsListNode,
  TableOfContentsTextNode,
} from '../rehypeTableOfContents';

interface Props {
  tableOfContents: TableOfContentsListNode;
}

export const BlogTableOfContents = ({ tableOfContents }: Props) => {
  return (
    <section className="px-8">
      <h2 className="text-fog-900 flex items-center gap-2 mb-8">
        <BookOpenIcon className="inline-block w-5" />{' '}
        <span className="inline-block font-sans text-sm font-semibold">
          In this article
        </span>
      </h2>
      <div className="-ml-4 -mt-4">
        <BlogTableOfContentsNode node={tableOfContents} />
      </div>
    </section>
  );
};

interface BlogTableOfContentsNodeProps {
  node:
    | TableOfContentsListNode
    | TableOfContentsListItemNode
    | TableOfContentsTextNode;
}

const BlogTableOfContentsNode = ({ node }: BlogTableOfContentsNodeProps) => {
  const router = useRouter();
  const [currentHash, setCurrentHash] = useState<string | null>(null);

  useEffect(() => {
    setCurrentHash(router.asPath.split('#')[1]);
  }, [router]);

  switch (node.type) {
    case 'list': {
      return (
        <ol className="flex flex-col gap-4 pl-4 mt-4">
          {node.children.map((child, index) => (
            <BlogTableOfContentsNode node={child} key={index} />
          ))}
        </ol>
      );
    }
    case 'item':
      return (
        <li>
          {node.children.map((child, index) => (
            <BlogTableOfContentsNode node={child} key={index} />
          ))}
        </li>
      );
    case 'text': {
      const isActive = currentHash === node.id;

      return (
        <Link
          href={node.href}
          className={`inline-block font-sans text-sm font-medium transition-colors -m-1 p-1 hover:text-fog-900 ${
            isActive ? 'text-fog-900' : 'text-fog-500'
          }`}
        >
          {node.value}
        </Link>
      );
    }
    default:
      assertUnreachable(node);
      return null;
  }
};
